import React from 'react';
import { Link } from 'react-router-dom';
import {
    BookOpen,
    Target,
    Briefcase,
    Award,
    GraduationCap,
    Hospital,
    FlaskConical,
    ChevronRight
} from 'lucide-react';
import Footer from '../../../components/Footer';
import heroimg from '../../../assets/schools/pharma/M pharma P Chemistry.jpg';
import HowToApplySection from '../../../components/HowToApplySection';

// Placeholder for importing images - replace with actual image paths
const heroImage = heroimg;

const MasterPharmacyPharmaceuticalChemistry = () => {
    const programSections = [
        {
            title: "Program Outcomes",
            items: [
                "Provide in-depth understanding of the chemical properties, synthesis, mechanism of action, and structure-activity relationships (SAR) of pharmaceutical compounds.",
                "Equip students with knowledge and techniques in drug design, medicinal chemistry, and computational modeling to contribute to new drug development.",
                "Train students in advanced synthetic organic chemistry, analytical techniques (e.g., HPLC, NMR, MS), and spectroscopic methods used in drug analysis and quality control.",
                "Foster independent research capabilities in areas like drug synthesis, natural product chemistry, or structure elucidation, encouraging original thinking and innovation.",
                "Develop awareness of regulatory guidelines, chemical stability, impurity profiling, and documentation for compliance in the pharmaceutical industry.",
                "Cultivate critical thinking to analyze complex chemical problems related to drug development, formulation, and interaction mechanisms.",
                "Instil strong ethical standards, laboratory safety practices, and professional responsibility in conducting and reporting scientific work.",
                "Enhance communication, scientific writing, and presentation skills, preparing students to contribute effectively in academic, industrial, and research environments."
            ],
            icon: <Target className="text-emerald-700" size={40} />
        },
        {
            title: "Career Prospects",
            items: [
                "Medicinal Chemist / Drug Discovery Scientist",
                "Analytical Chemist / Quality Control (QC) Analyst",
                "Research Scientist (Pharmaceutical or Chemical Research)",
                "Regulatory Affairs Associate",
                "Production & Manufacturing Chemist",
                "Academia / Teaching",
                "Opportunities Abroad"
            ],
            icon: <Briefcase className="text-emerald-700" size={40} />
        }
    ];

    const uniqueSellingPoints = [
        "Core Expertise in Drug Molecule Design & Synthesis",
        "Hands-on training in modern analytical tools such as HPLC, UV-Vis, FTIR",
        "Emphasizes original research, literature review, and dissertation work",
        "Direct Relevance to R&D in Pharma and Biotech"
    ];

    return (
        <div className="bg-emerald-50">
            <div className="bg-white text-emerald-900 font-sans mx-4 md:mx-8 lg:mx-16 my-4 mt-[70px]">
                {/* Hero Section */}
                <div className="relative bg-gradient-to-r from-emerald-50 to-white">
                    <div className="container mx-auto px-4 py-16 md:py-24 flex flex-col-reverse md:flex-row items-center">
                        <div className="md:w-1/2 space-y-6">
                            <h1 className="text-4xl md:text-5xl font-bold text-emerald-800 leading-tight">
                                Master in Pharmacy (M.Pharm – Pharmaceutical Chemistry)
                                <span className="block text-lg mt-2 font-medium text-emerald-600">
                                    Maya Devi University, Dehradun
                                </span>
                            </h1>
                            <p className="text-emerald-700 text-lg leading-relaxed">
                                The M.Pharm in Pharmaceutical Chemistry is a postgraduate program focusing on the design, synthesis, analysis, and evaluation of drug molecules. It provides a strong foundation in the chemical and biological aspects of pharmaceuticals, preparing students for careers in drug development, quality control, regulatory affairs, and research.
                            </p>
                            <div className="flex space-x-4">
                                <Link
                                    to="https://admission.maya.edu.in/"
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-full 
                           transition duration-300 flex items-center group"
                                >
                                    Apply Now
                                    <ChevronRight
                                        className="ml-2 group-hover:translate-x-1 transition"
                                        size={20}
                                    />
                                </Link>
                            </div>
                        </div>
                        <div className="md:w-1/2 mb-8 md:mb-0 m-10">
                            <img
                                src={heroImage}
                                alt="Pharmaceutical Chemistry Program"
                                className="rounded-xl shadow-lg hover:scale-105 transition duration-300"
                            />
                        </div>
                    </div>
                </div>

                {/* Eligibility and Program Details */}
                <div className="container mx-auto px-4 py-16 bg-emerald-50">
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-white p-6 rounded-xl shadow-md">
                            <h2 className="text-2xl font-bold text-emerald-800 mb-4">
                                <GraduationCap className="inline mr-3 text-emerald-600" size={30} />
                                Eligibility Criteria
                            </h2>
                            <ul className="space-y-3 text-emerald-700">
                                <li>• Minimum age: 17 years as of 31st December</li>
                                <li>• 10+2 with Physics and Chemistry as compulsory subjects</li>
                                <li>• Mathematics or Biology from a recognized board</li>
                                <li>• Minimum 50% marks for General category</li>
                                <li>• Minimum 45% marks for SC/ST category</li>
                            </ul>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-md">
                            <h2 className="text-2xl font-bold text-emerald-800 mb-4">
                                <Hospital className="inline mr-3 text-emerald-600" size={30} />
                                Program Details
                            </h2>
                            <table className="w-full text-emerald-700">
                                <tbody>
                                    <tr>
                                        <td className="font-semibold">Duration:</td>
                                        <td>2 Years</td>
                                    </tr>
                                    <tr>
                                        <td className="font-semibold">Approved By:</td>
                                        <td>Pharmacy Council of India (PCI)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Program Sections */}
                <div className="container mx-auto px-4 py-16">
                    <div className="grid md:grid-cols-2 gap-8">
                        {programSections.map((section, index) => (
                            <div
                                key={index}
                                className="bg-emerald-50 p-6 rounded-xl shadow-md hover:shadow-lg transition"
                            >
                                <div className="flex items-center mb-4">
                                    {section.icon}
                                    <h3 className="text-2xl font-bold ml-4 text-emerald-800">
                                        {section.title}
                                    </h3>
                                </div>
                                <ul className="space-y-2 text-emerald-700">
                                    {section.items.map((item, idx) => (
                                        <li key={idx} className="flex items-start">
                                            <span className="mr-2 text-emerald-600">•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* USPs Section */}
                <div className="container mx-auto px-4 py-16">
                    <h2 className="text-3xl font-bold text-center text-emerald-800 mb-12">
                        Why Choose Us
                    </h2>
                    <div className="max-w-3xl mx-auto">
                        <div className="bg-emerald-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition">
                            <div className="text-emerald-600 text-4xl mb-6 flex justify-center">
                                <Award size={60} />
                            </div>
                            <h3 className="text-2xl font-bold text-emerald-800 text-center mb-6">
                                Our USPs
                            </h3>
                            <ul className="space-y-3">
                                {uniqueSellingPoints.map((usp, index) => (
                                    <li
                                        key={index}
                                        className="flex items-center text-emerald-700 text-lg"
                                    >
                                        <div className="mr-3 text-emerald-600">•</div>
                                        {usp}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            {/* How to Apply Section */}
            <HowToApplySection/>
            
            <div className="bg-emerald-50">
                <Footer />
            </div>
        </div>
    );
};

export default MasterPharmacyPharmaceuticalChemistry;